import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'external/infiniteScroll/app'
import { searchLogo } from 'images'
import {
  PlaylistSort,
  getOptionDisplays,
  getValueFromDisplay,
  getDisplayFromValue
} from 'constants/enums'
import { PlayListService } from 'services'
import ModalActions from 'reducers/Modal'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import { MainLayout } from 'containers'
import {
  PlaylistCell,
  InputSelectDropdown,
  CollectionContent
} from 'components'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  ResponsiveBreakpointsProvider,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'
import {
  Row,
  CreateButton,
  Icon,
  Label,
  PlaylistGrid
} from './Playlist.styles'
import { getPlaylistData } from './selectors'
import PlaylistWithRollout from './PlaylistWithRollout'

export const getLegacyItemsPerRow = (windowWidth) =>
  Math.max(1, Math.min(Math.floor(windowWidth / 400), 7))

export const PlaylistContent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const scrollView = useRef(null)
  const [itemsPerRow, setItemsPerRow] = useState(2)
  const [selectedPlaylist, setSelectedPlaylist] = useState(null)
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const isDesktopV2 = useDesktopMediaQuery()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2

  const {
    playlists,
    page,
    isIpadView,
    loading,
    totalItemsCount,
    sort,
    searchToken,
    queueItems
  } = useSelector(getPlaylistData)

  const updateData = useCallback(
    (page) => {
      dispatch(PlaylistThunks.list({ page, searchToken, sort }))
    },
    [searchToken, sort]
  )

  const calculateItemsPerRow = useCallback(() => {
    setItemsPerRow(getLegacyItemsPerRow(window.innerWidth))
  }, [])

  useEffect(() => {
    updateData(1)
  }, [])

  useEffect(() => {
    if (responsiveBreakpointsV2) {
      return undefined
    }

    calculateItemsPerRow()
    window.addEventListener('resize', calculateItemsPerRow)
    return () => window.removeEventListener('resize', calculateItemsPerRow)
  }, [calculateItemsPerRow, responsiveBreakpointsV2])

  // Redux remains the rollback source until the App shell phase retires it.
  const showCompactInteractions = responsiveBreakpointsV2
    ? !isDesktopV2
    : isIpadView

  const onLoadMore = useCallback(() => {
    if (!loading) {
      dispatch(PlaylistThunks.list({ page: page + 1 }))
    }
  }, [page, loading])

  const handleCreateButton = () => {
    dispatch(ModalActions.togglePlaylistModal())
  }

  const handleSearchChange = (event) => {
    const searchToken = event.target.value
    dispatch(PlaylistActions.updateSearchToken(searchToken))
    if (searchToken.length === 0) {
      updateData(1)
    }
  }

  const handlePlayClick = (playlist) => {
    PlayListService.getTracklist(playlist.playlistid)
      .then((response) => {
        dispatch(
          PlayQueueThunks.playPlaylist(
            {
              ...playlist,
              songs: response.data.items
            },
            { source: PLAY_SOURCES.PLAYLISTS, trigger: PLAY_TRIGGERS.TILE }
          )
        )
      })
      .catch((e) => console.warn(e))
  }

  const toggleStarClick = (playlist) => {
    if (playlist.pinned) {
      dispatch(PlaylistThunks.unpin(playlist))
    } else {
      dispatch(PlaylistThunks.pin(playlist))
    }
  }

  totalItemsCount === null ? true : totalItemsCount > playlists.length

  const renderSearchBar = (isCompactView) => {
    return (
      <div className="flex flex-row items-center">
        <div className="flex flex-row items-center h-8 rounded-full bg-white font-header transition mr-4 w-72 px-4">
          <input
            value={searchToken}
            type="search"
            placeholder={t('home.search')}
            className="text-sm-placeholder focus:outline-none w-full"
            onChange={handleSearchChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                updateData(1)
              }
            }}
          />
        </div>
        {!isCompactView && (
          <button
            type="button"
            className={`${
              searchToken === '' ? 'opacity-50 cursor-default' : 'opacity-100'
            } slowTransition focus:outline-none`}
            onClick={() => updateData(1)}
          >
            <img src={searchLogo} className="w-8" alt="search button" />
          </button>
        )}
      </div>
    )
  }

  const renderSubHeader = () => {
    return (
      <>
        <Row className="mb-12">
          <Row>
            {renderSearchBar(showCompactInteractions)}
            <InputSelectDropdown
              value={sort ? getDisplayFromValue(PlaylistSort, sort) : ''}
              options={getOptionDisplays(PlaylistSort)}
              className="rounded-sm w-32 ml-4"
              t={t}
              onChange={(newValue) => {
                const value = getValueFromDisplay(PlaylistSort, newValue)
                dispatch(PlaylistActions.updateSort(value))
                updateData(1)
              }}
            />
          </Row>
          <CreateButton onClick={handleCreateButton} className="pr-10">
            <Icon className="icon">+</Icon>
            <Label className="label">{t('playlist.create-new')}</Label>
          </CreateButton>
        </Row>
      </>
    )
  }

  const renderTable = () => {
    return (
      <InfiniteScroll
        ref={scrollView}
        dataLength={playlists.length}
        next={onLoadMore}
        loader={<h4>Loading...</h4>}
        endMessage={<p style={{ textAlign: 'center' }}>You have seen it all</p>}
      >
        <PlaylistGrid $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          {playlists.map((item) => (
            <div
              key={item.playlistid}
              className={responsiveBreakpointsV2 ? undefined : 'inline-block'}
              style={
                responsiveBreakpointsV2
                  ? undefined
                  : { width: `${100 / itemsPerRow}%` }
              }
            >
              <PlaylistCell
                playlist={item}
                isIpadView={showCompactInteractions}
                selectedPlaylist={selectedPlaylist}
                hasPlayQueue={queueItems.length > 0}
                toggleStarClick={toggleStarClick}
                setSelectedPlaylist={setSelectedPlaylist}
                onPlayClick={handlePlayClick}
              />
            </div>
          ))}
        </PlaylistGrid>
      </InfiniteScroll>
    )
  }

  return (
    <div
      className="contents"
      data-responsive-breakpoints={responsiveBreakpointsVersion}
      data-responsive-breakpoints-page={RESPONSIVE_BREAKPOINT_PAGES.PLAYLIST}
    >
      <CollectionContent
        title={t('collections.playlists')}
        table={renderTable()}
        hasGradient
        subHeader={renderSubHeader()}
      />
    </div>
  )
}

const Playlist = () => (
  <ResponsiveBreakpointsProvider value={RESPONSIVE_BREAKPOINT_VERSIONS.LEGACY}>
    <MainLayout
      content={
        <PlaylistWithRollout>
          <PlaylistContent />
        </PlaylistWithRollout>
      }
    />
  </ResponsiveBreakpointsProvider>
)

export default Playlist
